import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import PropTypes from "prop-types";
import { maxFileSize } from "../../variables/Constants";
import { useTranslation } from "react-i18next";
import { HandleMessageError } from "../Notifications/APIErrorHandler";
import { FaCloudUploadAlt, FaFileAlt, FaTrashAlt, FaEye, FaSyncAlt } from "react-icons/fa";
import { decodeFileName } from "../Utils/Commons";

export const CustomFileUpload = (props) => {
  const {
    name,
    value,
    placeholder,
    onChange,
    disabled,
    onDownload,
    onRemove,
    accept,
    showRemove,
    allowReupload = true,
  } = props;

  const { t } = useTranslation();
  const [file, setFile] = useState(value || {});
  const [isDragging, setIsDragging] = useState(false);

  const handleFileDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const attach = acceptedFiles.map((f) =>
        Object.assign({}, {
          file: f,
          fileName: f.name || f.path,
          size: f.size,
        })
      );
      setFile(attach[0]);
      if (onChange) {
        onChange(name, attach[0]);
      }
    }
  };

  const hasFile = file && file.fileName;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleFileDrop,
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach((rejectedFile) => {
        const fileToCheck = rejectedFile.file || rejectedFile;
        if (fileToCheck.size > maxFileSize) {
          HandleMessageError(
            t("files.maxSize_Error", {
              name: fileToCheck.name,
              maxSize: maxFileSize * 0.000001,
            })
          );
        }
      });
    },
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    multiple: false,
    accept: accept,
    maxSize: maxFileSize,
    noClick: hasFile,
    noKeyboard: hasFile,
    disabled: disabled,
  });

  useEffect(() => {
    if (value) {
      setFile(value);
    } else {
      setFile({});
    }
  }, [value]);

  const handleRemove = () => {
    if (onRemove) {
      onRemove(name, file);
      return;
    }
    if (onChange) {
      onChange(name, null);
    }
    setFile({});
  };

  const handleView = () => {
    if (onDownload) onDownload(file.fileName);
  };

  const handleReupload = () => {
    open();
  };

  const displayFileName = decodeFileName(file?.fileName);
  const isActive = isDragActive || isDragging;

  return (
    <div style={{ width: "100%" }}>
      <div
        {...getRootProps()}
        style={{
          minHeight: hasFile ? "auto" : "100px",
          borderRadius: "8px",
          cursor: hasFile ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: hasFile ? "12px 16px" : "16px",
          position: "relative",
          overflow: "hidden",
          border: isActive ? "1px dashed #2196f3" : "1px dashed #ccc",
          backgroundColor: isActive ? "#f5f9ff" : "#fafafa",
          transition: "all 0.2s ease"
        }}
      >
        <input {...getInputProps()} disabled={disabled} />
        
        {!hasFile ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "6px" }}>
            <FaCloudUploadAlt style={{ fontSize: "36px", color: isActive ? "#2196f3" : "#aaa", transition: "all 0.2s ease" }} />
            <p style={{ fontSize: "14px", fontWeight: "500", color: "#555", margin: "4px 0 0 0" }}>{placeholder || "Arrastra un archivo aquí o haz clic para seleccionar"}</p>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: "12px", position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
              <div style={{ width: "36px", height: "36px", backgroundColor: "#e3e3e3", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FaFileAlt style={{ fontSize: "18px", color: "#666" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: 0, flex: 1 }}>
                <a 
                  style={{ fontSize: "14px", fontWeight: "400", color: "#247dbd", cursor: "pointer", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}
                  onClick={(e) => { e.stopPropagation(); handleView(); }}
                  title={displayFileName}
                >
                  {displayFileName}
                </a>
                {file.size && (
                  <span style={{ fontSize: "11px", color: "#999" }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            </div>
            
            <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
              <button
                type="button"
                style={{ width: "32px", height: "32px", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s ease", fontSize: "13px", backgroundColor: "#e3f2fd", color: "#1976d2" }}
                onClick={(e) => { e.stopPropagation(); handleView(); }}
                title="Ver archivo"
              >
                <FaEye />
              </button>
              
              {allowReupload && !disabled && (
                <button
                  type="button"
                  style={{ width: "32px", height: "32px", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s ease", fontSize: "13px", backgroundColor: "#e8f5e9", color: "#388e3c" }}
                  onClick={(e) => { e.stopPropagation(); handleReupload(); }}
                  title="Subir nueva versión"
                >
                  <FaSyncAlt />
                </button>
              )}
              
              {showRemove && !disabled && (
                <button
                  type="button"
                  style={{ width: "32px", height: "32px", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s ease", fontSize: "13px", backgroundColor: "#ffebee", color: "#c62828" }}
                  onClick={(e) => { e.stopPropagation(); handleRemove(); }}
                  title="Eliminar archivo"
                >
                  <FaTrashAlt />
                </button>
              )}
            </div>
            
            {isActive && (
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(33, 150, 243, 0.9)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", borderRadius: "8px", zIndex: 10 }}>
                <FaCloudUploadAlt style={{ fontSize: "28px", color: "white" }} />
                <span style={{ color: "white", fontSize: "13px", fontWeight: "500" }}>Suelta para reemplazar</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

CustomFileUpload.defaultProps = {
  accept: "image/*,.pdf,.docx,.doc,.xls,.xlsx",
  showRemove: true,
  allowReupload: true,
};

CustomFileUpload.propTypes = {
  accept: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onDownload: PropTypes.func,
  onRemove: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.object,
  showRemove: PropTypes.bool,
  allowReupload: PropTypes.bool,
  disabled: PropTypes.bool,
};
